import { useEffect } from "react";
import { Box, Button, CircularProgress, Stack, TextField } from "@mui/material";
import { CustomAutocomplete } from "@/shared/components/ui";
import { UsuariosSistemaGetExtras } from "@/shared/interfaces";
import {
  useFetchGetAreascontrolcambios,
  useFetchPatchCambiarArea,
} from "@/shared/actions";
import { Areascontrolcambios } from "../../types";
import { useForm } from "react-hook-form";
import ModalForm from "@/shared/components/ui/ModalForm";

// El row de aprobadores trae el área aunque el tipo base no la declare
type AprobadorRow =
  | (UsuariosSistemaGetExtras & {
      area?: { areaId?: string; nombre?: string } | null;
    })
  | null;

type ModalEditarAreaAprobadorProps = {
  open: boolean;
  onClose: () => void;
  aprobador: AprobadorRow;
};

type FormValues = {
  areaId: string;
};

export default function ModalEditarAreaAprobador({
  open,
  onClose,
  aprobador,
}: ModalEditarAreaAprobadorProps) {
  const { data: areasData, isLoading: isLoadingAreas } =
    useFetchGetAreascontrolcambios({ page: 0, size: 1000 });
  const cambiarAreaMutation = useFetchPatchCambiarArea();

  const form = useForm<FormValues>({
    defaultValues: { areaId: "" },
  });
  const { handleSubmit, control, reset } = form;

  // Precarga el área actual cada vez que se abre el modal
  useEffect(() => {
    if (open) {
      reset({ areaId: aprobador?.area?.areaId ?? "" });
    }
  }, [open, aprobador, reset]);

  const nombreUsuario =
    aprobador?.profile?.razon_social ??
    aprobador?.full_name ??
    aprobador?.username ??
    "";

  const onSubmit = (values: FormValues) => {
    if (!aprobador?.aprobadorAreaId) return;
    cambiarAreaMutation.mutate(
      { id: aprobador.aprobadorAreaId, areaId: values.areaId },
      { onSuccess: onClose }
    );
  };

  return (
    <ModalForm
      open={open}
      onClose={onClose}
      title={
        aprobador ? `Editar área de ${nombreUsuario}` : "Editar área del aprobador"
      }
      maxWidth={500}
    >
      <Box component={"form"} onSubmit={handleSubmit(onSubmit)} sx={{ p: 1 }}>
        <Stack spacing={3}>
          {/* Usuario en solo lectura (razón social) */}
          <TextField
            label="Usuario"
            value={nombreUsuario}
            InputProps={{ readOnly: true }}
            fullWidth
          />

          <CustomAutocomplete<Areascontrolcambios>
            name="areaId"
            label="Área"
            control={control}
            options={areasData?.data || []}
            valueKey="nombre"
            actualValueKey="areaId"
            optionLabelForEdit={aprobador?.area?.nombre ?? ""}
            defaultValue={aprobador?.area?.areaId ?? ""}
            isLoadingData={isLoadingAreas}
            error={form.formState.errors.areaId}
            helperText={form.formState.errors.areaId?.message ?? ""}
            required
          />

          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button
              variant="outlined"
              onClick={onClose}
              disabled={cambiarAreaMutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={cambiarAreaMutation.isPending}
            >
              {cambiarAreaMutation.isPending ? (
                <CircularProgress size={20} />
              ) : (
                "Guardar"
              )}
            </Button>
          </Box>
        </Stack>
      </Box>
    </ModalForm>
  );
}
